import { useInfiniteQuery } from '@tanstack/react-query';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import api from '../../api/axiosInstance';
import ListItems from '../list';
import { OperationData } from '../modal/modalAddOperation';
import { Loader } from '../loader';
import Box from '@mui/material/Box';

type BlockType = 'all' | 'income' | 'expense' | 'category';

interface BlockProps {
  type: BlockType;
  page?: 'home' | null;
}

type ApiOp = Omit<OperationData, 'type'> & Partial<Pick<OperationData, 'type'>>;

const LIMIT = 5;

export default function Block({ type, page }: BlockProps) {
  const isHome = page === 'home';

  const endpoint =
    type === 'all'
      ? '/operations'
      : type === 'income'
        ? '/incomes'
        : type === 'expense'
          ? '/expenses'
          : '/operations';

  const normalize = (rows: ApiOp[]): OperationData[] =>
    rows.map(row => {
      if (row.type === 'income' || row.type === 'expense') {
        return row as OperationData;
      }

      const inferred: 'income' | 'expense' =
        (row as any).categoryincomeId != null ? 'income' : 'expense';

      return { ...(row as OperationData), type: inferred };
    });

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<ApiOp[], Error, OperationData[]>({
      queryKey: [type, isHome ? 'home' : 'infinite'],
      initialPageParam: 0,

      queryFn: async ({ pageParam }) => {
        const offset = isHome ? 0 : pageParam;
        const res = await api.get<ApiOp[]>(`${endpoint}?limit=${LIMIT}&offset=${offset}`);
        return res.data;
      },

      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < LIMIT) return undefined;
        return allPages.length * LIMIT;
      },

      select: data => {
        const flat = data.pages.flat();

        if (type === 'income') {
          return flat.map(r => ({ ...(r as OperationData), type: 'income' }));
        }

        if (type === 'expense') {
          return flat.map(r => ({ ...(r as OperationData), type: 'expense' }));
        }

        return normalize(flat);
      },
    });

  if (isLoading) {
    return (
      <Typography>
        <Loader />
      </Typography>
    );
  }

  if (isError) {
    return <Typography color="error">Ошибка</Typography>;
  }

  const listType: 'all' | 'income' | 'expense' | 'category' = type === 'expense' ? 'expense' : type;

  return (
    <>
      <ListItems type={listType} items={data ?? []} page={page} />

      {!isHome && hasNextPage && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Button onClick={() => fetchNextPage()} disabled={isFetchingNextPage} variant="outlined">
            {isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
          </Button>
        </Box>
      )}
    </>
  );
}
